import { KubernetesSecurityFormValues } from '@/react/portainer/policies/components/kubernetes/security/types';

import { FormSection } from '@@/form-components/FormSection';
import { Badge } from '@@/Badge';

function SettingsList({ children }: { children: React.ReactNode }) {
  return (
    <div className="space-y-1 overflow-hidden rounded-lg border border-gray-5 th-dark:border-gray-7">
      {children}
    </div>
  );
}

function SettingItem({
  label,
  value,
}: {
  label: string;
  value: boolean | undefined;
}) {
  return (
    <div className="flex items-center justify-between bg-gray-2 px-4 py-3 th-dark:bg-gray-iron-10">
      <span className="text-sm">{label}</span>
      <Badge type={value ? 'success' : 'muted'}>
        {value ? 'Enabled' : 'Disabled'}
      </Badge>
    </div>
  );
}

function SubSettingItem({
  label,
  value,
}: {
  label: string;
  value: boolean | undefined;
}) {
  return (
    <div className="flex items-center justify-between bg-gray-3 px-4 py-2 pl-8 th-dark:bg-gray-9">
      <span className="text-muted text-sm">{label}</span>
      <Badge type={value ? 'success' : 'muted'}>
        {value ? 'Enabled' : 'Disabled'}
      </Badge>
    </div>
  );
}

function ValueRow({
  label,
  value,
  className = '',
}: {
  label: string;
  value: string;
  className?: string;
}) {
  return (
    <div
      className={`flex items-center justify-between bg-gray-3 px-4 py-2 pl-8 th-dark:bg-gray-9 ${className}`.trim()}
    >
      <span className="text-muted text-sm">{label}</span>
      <span className="text-sm font-medium">{value}</span>
    </div>
  );
}

type IdRange = { min: number; max: number };

type IdRestrictionType =
  | 'RunAsAny'
  | 'MustRunAs'
  | 'MustRunAsNonRoot'
  | 'MustRunAsRange';

function isIdRestrictionType(value: string): value is IdRestrictionType {
  return (
    value === 'RunAsAny' ||
    value === 'MustRunAs' ||
    value === 'MustRunAsNonRoot' ||
    value === 'MustRunAsRange'
  );
}

function formatIdRestriction(
  type: string | undefined,
  ranges?: ReadonlyArray<IdRange>
): string {
  if (!type) {
    return 'Any';
  }

  if (!isIdRestrictionType(type)) {
    return type;
  }

  if ((type !== 'MustRunAs' && type !== 'MustRunAsRange') || !ranges?.length) {
    return type;
  }

  return `${type} (${ranges.map((r) => `${r.min}-${r.max}`).join(', ')})`;
}
interface Props {
  formData: KubernetesSecurityFormValues;
}

/**
 * Read-only display of Security policy settings with clean visual styling.
 */
export function SecurityPolicyReadOnly({ formData }: Props) {
  return (
    <div className="space-y-6">
      <FormSection title="Security">
        <SettingsList>
          <SettingItem
            label="Restrict access to the default namespace"
            value={formData.restrictDefaultNamespace}
          />
          <SettingItem
            label="Restrict secret contents access for non-admins"
            value={formData.restrictSecrets}
          />
        </SettingsList>
      </FormSection>

      <FormSection title="Pod Security Constraints">
        <SettingsList>
          <SettingItem
            label="Enable pod security constraints"
            value={formData.enablePodSecurityConstraints}
          />
        </SettingsList>
      </FormSection>

      {formData.enablePodSecurityConstraints && (
        <FormSection title="Constraints">
          <SettingsList>
            <SettingItem
              label="Restrict running privileged containers"
              value={formData.restrictPrivilegedContainers}
            />
            <SettingItem
              label="Restrict host namespace"
              value={formData.restrictHostNamespace}
            />
            <SettingItem
              label="Restrict host networking ports"
              value={formData.restrictHostNetworkingPorts}
            />
            {formData.restrictHostNetworkingPorts && (
              <>
                <SubSettingItem
                  label="Host network"
                  value={formData.hostNetwork}
                />
                {formData.hostPortMin !== undefined &&
                  formData.hostPortMax !== undefined && (
                    <ValueRow
                      label="Host port range"
                      value={`${formData.hostPortMin} - ${formData.hostPortMax}`}
                    />
                  )}
              </>
            )}
            <SettingItem
              label="Restrict volume types"
              value={formData.restrictVolumeTypes}
            />
            {formData.restrictVolumeTypes &&
              formData.allowedVolumeTypes?.length > 0 && (
                <ValueRow
                  label="Allowed volume types"
                  value={formData.allowedVolumeTypes.join(', ')}
                />
              )}
            <SettingItem
              label="Restrict host filesystem paths"
              value={formData.restrictHostFilesystemPaths}
            />
            {formData.restrictHostFilesystemPaths &&
              formData.allowedHostPaths?.length > 0 && (
                <ValueRow
                  label="Allowed host paths"
                  value={formData.allowedHostPaths
                    .map(
                      (p) =>
                        `${p.pathPrefix}${p.readonly ? ' (read-only)' : ''}`
                    )
                    .join(', ')}
                />
              )}
            <SettingItem
              label="Restrict drivers"
              value={formData.restrictDrivers}
            />
            {formData.restrictDrivers &&
              formData.allowedFlexVolumeDrivers?.length > 0 && (
                <ValueRow
                  label="Allowed FlexVolume drivers"
                  value={formData.allowedFlexVolumeDrivers.join(', ')}
                />
              )}
            <SettingItem
              label="Require read-only root filesystem"
              value={formData.requireReadOnlyRootFilesystem}
            />
            <SettingItem
              label="Restrict user and group IDs"
              value={formData.restrictUserAndGroupIds}
            />
            {formData.restrictUserAndGroupIds && (
              <>
                <ValueRow
                  label="Run as User"
                  value={formatIdRestriction(
                    formData.runAsUserType,
                    formData.runAsUserRanges
                  )}
                />
                <ValueRow
                  label="Run as Group"
                  value={formatIdRestriction(
                    formData.runAsGroupType,
                    formData.runAsGroupRanges
                  )}
                />
                <ValueRow
                  label="Supplemental groups"
                  value={formatIdRestriction(
                    formData.supplementalGroupsType,
                    formData.supplementalGroupsRanges
                  )}
                />
                <ValueRow
                  label="FS group"
                  value={formatIdRestriction(
                    formData.fsGroupType,
                    formData.fsGroupRanges
                  )}
                />
              </>
            )}
            <SettingItem
              label="Restrict escalation to root privileges"
              value={formData.restrictEscalationToRootPrivileges}
            />
            <SettingItem
              label="Restrict Linux capabilities"
              value={formData.restrictLinuxCapabilities}
            />
            {formData.restrictLinuxCapabilities && (
              <>
                {formData.allowedCapabilities?.length > 0 && (
                  <ValueRow
                    label="Allowed capabilities"
                    value={formData.allowedCapabilities.join(', ')}
                  />
                )}
                {formData.requiredDropCapabilities?.length > 0 && (
                  <ValueRow
                    label="Required drop capabilities"
                    value={formData.requiredDropCapabilities.join(', ')}
                  />
                )}
                {formData.defaultAddCapabilities?.length > 0 && (
                  <ValueRow
                    label="Default add capabilities"
                    value={formData.defaultAddCapabilities.join(', ')}
                  />
                )}
              </>
            )}
            <SettingItem
              label="Restrict SELinux security context"
              value={formData.restrictSeLinuxSecurityContext}
            />
            <SettingItem
              label="Restrict Proc Mount types"
              value={formData.restrictProcMountTypes}
            />
            {formData.restrictProcMountTypes && (
              <ValueRow
                label="Proc mount type"
                value={formData.procMountType}
              />
            )}
            <SettingItem
              label="Restrict AppArmor profiles"
              value={formData.restrictAppArmorProfiles}
            />
            {formData.restrictAppArmorProfiles &&
              formData.allowedAppArmorProfiles?.length > 0 && (
                <ValueRow
                  label="Allowed AppArmor profiles"
                  value={formData.allowedAppArmorProfiles.join(', ')}
                />
              )}
            <SettingItem
              label="Restrict seccomp profiles"
              value={formData.restrictSeccompProfiles}
            />
            {formData.restrictSeccompProfiles &&
              formData.allowedSeccompProfiles?.length > 0 && (
                <ValueRow
                  label="Allowed seccomp profiles"
                  value={formData.allowedSeccompProfiles.join(', ')}
                />
              )}
            <SettingItem
              label="Restrict sysctl profiles"
              value={formData.restrictSysctlProfiles}
            />
            {formData.restrictSysctlProfiles &&
              formData.forbiddenSysctls?.length > 0 && (
                <ValueRow
                  label="Forbidden sysctls"
                  value={formData.forbiddenSysctls.join(', ')}
                />
              )}
          </SettingsList>
        </FormSection>
      )}
    </div>
  );
}
